"use client";

import {
  IconBrandJavascript,
  IconBrandReact,
  IconBrandSvelte,
  IconBrandVue,
} from "@tabler/icons-react";
import { registerDocsHighlighting } from "src/lib/DocsHighlighting";
import type { DocsUiLibrary } from "src/lib/DocsUiLibrary";
import { useExtracted } from "#i18n";
import DocsCodeTabs, { type DocsTabCode } from "./DocsCodeTabs";
import { useDocsUiLibrary } from "./DocsLanguageContext";

const TAB_ICONS = {
  react: IconBrandReact,
  vue: IconBrandVue,
  svelte: IconBrandSvelte,
  html: IconBrandJavascript,
} satisfies Record<DocsUiLibrary, unknown>;

export interface DocsUiLibraryTabsProps {
  /** The libraries the example offers, in tab order. */
  tabs: readonly DocsUiLibrary[];
  code: Partial<Record<DocsUiLibrary, DocsTabCode[]>>;
  /** Build-time tokens for the code, from `encodeDocsHighlighting`. */
  highlighting?: string;
  [dataAttribute: `data-${string}`]: string | undefined;
}

/**
 * The UI library tabs of a front-end docs example. The same tabs as the SDK language examples, with
 * the reader's library choice remembered and shared across examples in the same way.
 */
const DocsUiLibraryTabs = (props: DocsUiLibraryTabsProps) => {
  const { tabs, code, highlighting, ...dataAttributes } = props;
  registerDocsHighlighting(highlighting);
  const translateMessage = useExtracted();
  const { uiLibrary, selectUiLibrary } = useDocsUiLibrary();
  const labels: Record<DocsUiLibrary, string> = {
    react: translateMessage("React"),
    vue: translateMessage("Vue"),
    svelte: translateMessage("Svelte"),
    html: translateMessage("Plain JavaScript"),
  };
  const selected = tabs.includes(uiLibrary) ? uiLibrary : tabs[0]!;
  return (
    <DocsCodeTabs
      tabs={tabs.map((tab) => ({ value: tab, label: labels[tab], icon: TAB_ICONS[tab] }))}
      selected={selected}
      onSelect={selectUiLibrary}
      code={code}
      {...dataAttributes}
      data-docs-ui-library={selected}
    />
  );
};

export default DocsUiLibraryTabs;
